import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { WIZARD_STEP_COMPONENTS } from "@/components/mentor-profile/wizard-step-registry";
import {
  useMentorProfileReview,
  useSubmitMentorProfile,
  type MentorProfileChangeRequest,
  type MentorProfileDraft,
  type MentorProfileReviewStatus,
} from "@/lib/api/mentor-profile";
import { useMentorProfileDraft } from "@/lib/hooks/mentor-profile/use-mentor-profile-draft";
import { useMentorProfileEnabled } from "@/lib/hooks/use-mentor-profile";
import {
  PROFILE_SECTIONS,
  type WizardBuildStep,
} from "@/lib/mentor-profile/profile-sections";
import { firstFlaggedBuildStep } from "@/lib/mentor-profile/resolve-onboarding-entry";
import {
  canSubmitProfile,
  openChangeRequests,
} from "@/lib/mentor-profile/submit-readiness";
import {
  clearRevisionBaseline,
  readOrWriteRevisionBaseline,
} from "@/lib/mentor-profile/revision-baseline";
import { cn } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown, Clock, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

type SectionId = WizardBuildStep;

const SETTINGS_SECTION_LABEL: Partial<Record<SectionId, string>> = {
  profile_photo: "Match card photo",
};

export function MatchingProfileSettings({
  hidePageHeading = false,
}: {
  hidePageHeading?: boolean;
}) {
  const enabled = useMentorProfileEnabled();
  const { data: review, isPending, isError } = useMentorProfileReview(enabled);
  const submit = useSubmitMentorProfile();
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!enabled) {
    return (
      <p className="mt-6 font-body text-sm text-text-secondary">
        Matching profile editing is not enabled for this account yet.
      </p>
    );
  }

  if (isPending) {
    return (
      <div className="mt-8 flex items-center gap-2 font-body text-sm text-text-secondary">
        <Loader2 className="size-4 animate-spin" />
        Loading profile…
      </div>
    );
  }

  if (isError || !review) {
    return (
      <p className="mt-6 font-body text-sm text-states-error">
        Could not load your matching profile.
      </p>
    );
  }

  const openNotes = openChangeRequests(review.change_requests);
  const canEditInSettings =
    Boolean(review.published_snapshot) ||
    review.status === "changes_requested" ||
    openNotes.length > 0;

  if (!canEditInSettings) {
    return (
      <div className="mt-8 max-w-xl rounded-2xl border border-surfaces-z5 bg-surfaces-z3 p-6">
        <h2 className="font-heading text-xl font-bold text-text-primary">
          Finish your matching profile first
        </h2>
        <p className="mt-2 font-body text-sm leading-relaxed text-text-secondary">
          Settings editing is for after your profile is approved. Keep building
          it in onboarding, then you can tweak it here anytime.
        </p>
        <div className="mt-5">
          <Link to="/mentor-profile">
            <Button size="sm" fullWidth={false}>
              Continue onboarding
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Key on the revision round, not status — remounting on status would re-freeze
  // baseline against an already-edited draft.
  const revisionRoundKey = openNotes[0]?.created_at ?? "none";

  return (
    <MatchingProfileSettingsEditor
      key={`${review.id}-${revisionRoundKey}`}
      hidePageHeading={hidePageHeading}
      reviewId={review.id}
      revisionRoundKey={revisionRoundKey}
      initialDraft={review.draft}
      publishedSnapshot={review.published_snapshot}
      status={review.status}
      changeRequests={review.change_requests}
      reviewUpdatedAt={review.updated_at}
      submitPending={submit.isPending}
      submitError={submitError}
      onSubmit={async () => {
        setSubmitError(null);
        try {
          await submit.mutateAsync();
          clearRevisionBaseline(review.id, revisionRoundKey);
        } catch {
          setSubmitError("Could not submit your changes. Please try again.");
        }
      }}
    />
  );
}

function MatchingProfileSettingsEditor({
  reviewId,
  revisionRoundKey,
  initialDraft,
  publishedSnapshot,
  status,
  changeRequests,
  reviewUpdatedAt,
  submitPending,
  submitError,
  onSubmit,
  hidePageHeading,
}: {
  reviewId: string;
  revisionRoundKey: string;
  initialDraft: MentorProfileDraft;
  publishedSnapshot: MentorProfileDraft | null;
  status: MentorProfileReviewStatus;
  changeRequests: MentorProfileChangeRequest[];
  reviewUpdatedAt: string;
  submitPending: boolean;
  submitError: string | null;
  onSubmit: () => Promise<void>;
  hidePageHeading: boolean;
}) {
  const draftState = useMentorProfileDraft(initialDraft);
  const inReview = status === "submitted" || status === "in_review";
  const [openSection, setOpenSection] = useState<SectionId | null>(() =>
    inReview ? null : firstFlaggedBuildStep(changeRequests),
  );
  const [revisionBaseline] = useState(() => {
    if (openChangeRequests(changeRequests).length === 0) return null;
    return readOrWriteRevisionBaseline(
      reviewId,
      revisionRoundKey,
      initialDraft,
    );
  });
  const openNotes = useMemo(
    () => openChangeRequests(changeRequests),
    [changeRequests],
  );
  const needsChanges =
    status === "changes_requested" || openNotes.length > 0;
  const canSubmit = canSubmitProfile({
    status,
    draft: draftState.draft,
    publishedSnapshot,
    baseline: revisionBaseline,
    changeRequests,
    reviewUpdatedAt,
  });
  const name =
    draftState.draft.display_name || draftState.draft.first_name || "your";

  const Editor = openSection ? WIZARD_STEP_COMPONENTS[openSection] : null;
  const openMeta = openSection
    ? PROFILE_SECTIONS.find((s) => s.id === openSection)
    : null;
  const openReviewerNotes = useMemo(() => {
    if (!openMeta) return [];
    return openNotes
      .filter((n) => n.section === openMeta.reviewSection)
      .map((n) => n.note);
  }, [openMeta, openNotes]);

  const ctaLabel = submitPending
    ? "Submitting…"
    : inReview
      ? "In review"
      : !canSubmit
        ? "No changes made"
        : needsChanges
          ? "Resubmit for review"
          : "Submit for review";
  const ctaDisabled = submitPending || !canSubmit;

  return (
    <div className="grid gap-8 pb-10 lg:grid-cols-[minmax(0,1fr)_392px] lg:items-start">
      <div className="min-w-0">
        {hidePageHeading ? null : (
          <>
            <h2 className="font-heading text-2xl font-bold leading-8 text-text-primary">
              Editing {name}&apos;s Profile
            </h2>
            <p className="mt-2 max-w-xl font-body text-sm leading-[1.4] text-text-secondary">
              Everything teens see, in one place — your match card and your
              chat. Open a section to edit it; the preview shows exactly what
              teens get.
            </p>
          </>
        )}

        {inReview ? (
          <div className="mt-5 flex items-start gap-3 rounded-[22px] border border-amber-200 bg-amber-50 p-[15px]">
            <Clock
              className="mt-0.5 size-4 shrink-0 text-amber-900"
              strokeWidth={1.75}
              aria-hidden
            />
            <p className="font-body text-sm leading-[22.75px] text-amber-900">
              Your profile is in review. Edits save as you go and show up for
              the reviewer automatically — we&apos;ll email you when there&apos;s
              an update.
            </p>
          </div>
        ) : null}

        {needsChanges ? (
          <div className="mt-5 rounded-lg border border-brand-tangelo/30 bg-[#fff8f4] px-4 py-3 font-body text-sm text-text-primary">
            Some sections need changes before we can publish. Open the flagged
            sections below.
          </div>
        ) : null}

        <div
          className={cn(
            "flex flex-col gap-2",
            hidePageHeading && !inReview && !needsChanges ? "mt-0" : "mt-6",
          )}
        >
          {PROFILE_SECTIONS.map((section) => {
            const needs = openNotes.some(
              (n) => n.section === section.reviewSection,
            );
            const isOpen = openSection === section.id;
            const summary = section.summary(draftState.draft);

            return (
              <div
                key={section.id}
                className={cn(
                  "overflow-hidden rounded-[22px] border border-[#dfddd8] bg-white",
                  isOpen && "shadow-[0px_2px_4px_rgba(0,0,0,0.05)]",
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? null : section.id)}
                  className="flex w-full items-start gap-4 p-[17px] text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-heading text-base font-bold leading-6 text-text-primary">
                        {SETTINGS_SECTION_LABEL[section.id] ?? section.label}
                      </p>
                      {needs ? (
                        <span className="rounded-full bg-brand-tangelo/10 px-2 py-0.5 font-body text-[11px] font-medium text-brand-tangelo">
                          Needs changes
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                      {summary.checked ? (
                        <Check
                          className="size-[18px] shrink-0 text-states-success"
                          strokeWidth={2.5}
                        />
                      ) : null}
                      <p
                        className={cn(
                          "truncate font-body text-sm leading-5",
                          summary.empty
                            ? "text-text-placeholder"
                            : "text-text-secondary",
                        )}
                      >
                        {summary.text}
                      </p>
                    </div>
                  </div>
                  <span className="flex size-10 shrink-0 items-center justify-center">
                    <ChevronDown
                      className={cn(
                        "size-6 text-text-primary transition-transform",
                        isOpen && "rotate-180",
                      )}
                    />
                  </span>
                </button>

                {isOpen && Editor ? (
                  <div className="border-t border-[#dfddd8] bg-white px-5 pb-5 pt-5">
                    <p className="mb-4 font-body text-xs leading-relaxed text-text-secondary">
                      {section.hint}
                    </p>
                    <Editor
                      draft={draftState.draft}
                      saveState={draftState.saveState}
                      setField={draftState.setField}
                      setFields={draftState.setFields}
                      reviewerNotes={openReviewerNotes}
                      embedded
                      onContinue={() => setOpenSection(null)}
                      onBack={() => setOpenSection(null)}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex h-20 flex-col items-end justify-center gap-2">
          <Button
            size="lg"
            fullWidth={false}
            className="h-[53px] w-[345px] max-w-full"
            disabled={ctaDisabled}
            loading={submitPending}
            onPress={() => void onSubmit()}
          >
            {ctaLabel}
          </Button>
          {submitError ? (
            <p className="font-body text-sm text-states-error">{submitError}</p>
          ) : null}
        </div>
      </div>

      <div className="flex justify-center lg:sticky lg:top-8 lg:justify-center">
        <PhoneFrame>
          <MatchingProfilePreviewCard draft={draftState.draft} />
        </PhoneFrame>
      </div>
    </div>
  );
}
